/**
 * Homepage regulations list: column order and default visibility.
 */
import type { GridColumnVisibilityModel } from "@mui/x-data-grid-premium";
import {
  isDynamicAutoTagsField,
  type FieldConfig,
} from "../../../services/regulationsService";

/** Fixed list column order (product layout). Only shown when API returns the field. */
export const LIST_TABLE_COLUMN_ORDER: readonly string[] = [
  "regulation_id",
  "title",
  "version",
  "markets",
  "product_types",
  "impacted_concepts",
  "tags",
  "categories",
  "auto_tags",
  "modified_date",
  "release_date",
  "published_date",
  "effective_date",
  "synopsis_withouthtml",
  "related_regulations",
  "created_date",
  "received_date",
  "reference_insight_id",
];

/** Initially visible list columns (Manage Columns defaults). */
export const LIST_TABLE_DEFAULT_VISIBLE_FIELD_NAMES: readonly string[] = [
  "regulation_id",
  "title",
  "version",
  "markets",
  "product_types",
  "impacted_concepts",
  "tags",
  "auto_tags",
];

/** Excluded from list table even if returned by fields API. */
export const LIST_TABLE_EXCLUDED_FIELD_NAMES = new Set(["due_date"]);

/** When API returns alias field names for one column, prefer this field_name. */
const ROW_KEY_PREFERRED_FIELD_NAMES: Record<string, readonly string[]> = {
  synopsis: ["synopsis_withouthtml", "synopsis"],
  relatedItems: ["related_regulations", "related_items"],
  receivedDate: ["created_date", "received_date"],
};

export const FIELD_NAME_TO_ROW_KEY: Record<string, string> = {
  regulation_id: "id",
  title: "title",
  version: "version",
  markets: "markets",
  product_types: "productTypes",
  tags: "tags",
  categories: "tags",
  auto_tags: "autoTags",
  published_date: "publishedDate",
  release_date: "releaseDate",
  effective_date: "effectiveDate",
  synopsis_withouthtml: "synopsis",
  synopsis: "synopsis",
  modified_date: "modifiedDate",
  related_regulations: "relatedItems",
  related_items: "relatedItems",
  created_date: "receivedDate",
  received_date: "receivedDate",
  regulations_status: "status",
  impacted_concepts: "impactedConcepts",
  reference_insight_id: "referenceInsightId",
};

function snakeToCamel(fieldName: string): string {
  return fieldName.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

export function fieldNameToRowKey(fieldName: string): string {
  return FIELD_NAME_TO_ROW_KEY[fieldName] ?? snakeToCamel(fieldName);
}

const LIST_TABLE_DEFAULT_VISIBLE_ROW_KEYS = new Set(
  LIST_TABLE_DEFAULT_VISIBLE_FIELD_NAMES.map(fieldNameToRowKey),
);

function buildListFieldByRowKey(
  listPageFields?: FieldConfig[],
): Map<string, FieldConfig> {
  const map = new Map<string, FieldConfig>();
  if (!listPageFields?.length) return map;

  const listOnly = listPageFields.filter((field) =>
    field.pages.includes("list"),
  );
  for (const field of pickListTableFieldsInOrder(listOnly)) {
    map.set(fieldNameToRowKey(field.field_name), field);
  }
  return map;
}

function pickPreferredListField(candidates: FieldConfig[]): FieldConfig {
  const rowKey = fieldNameToRowKey(candidates[0].field_name);
  const prefs = ROW_KEY_PREFERRED_FIELD_NAMES[rowKey];
  if (prefs) {
    for (const name of prefs) {
      const match = candidates.find((c) => c.field_name === name);
      if (match) return match;
    }
  }
  return candidates[0];
}

function buildApiFieldGroups(
  listPageFields: FieldConfig[],
): Map<string, FieldConfig[]> {
  const groups = new Map<string, FieldConfig[]>();
  for (const field of listPageFields) {
    if (LIST_TABLE_EXCLUDED_FIELD_NAMES.has(field.field_name)) continue;
    const rowKey = fieldNameToRowKey(field.field_name);
    const existing = groups.get(rowKey) ?? [];
    existing.push(field);
    groups.set(rowKey, existing);
  }
  return groups;
}

function resolveListField(
  canonicalName: string,
  byName: Map<string, FieldConfig>,
  groups: Map<string, FieldConfig[]>,
): FieldConfig | undefined {
  let field = byName.get(canonicalName);
  if (!field && canonicalName === "related_regulations") {
    field = byName.get("related_items");
  }
  if (!field && canonicalName === "synopsis_withouthtml") {
    field = byName.get("synopsis");
  }
  if (!field && canonicalName === "tags") {
    field = byName.get("categories");
  }
  if (!field && canonicalName === "created_date") {
    field = byName.get("received_date");
  }
  if (!field && canonicalName === "received_date") {
    field = byName.get("created_date");
  }
  if (!field) return undefined;

  const rowKey = fieldNameToRowKey(field.field_name);
  const candidates = groups.get(rowKey);
  if (candidates?.length) return pickPreferredListField(candidates);
  return field;
}

/**
 * Column order: fixed LIST_TABLE_COLUMN_ORDER, then dynamic auto-tag fields
 * (`filter_field_name` contains "auto_tags") in API order.
 * Which columns exist: only fields returned by GET /regulations/fields (page_type=list).
 */
export function pickListTableFieldsInOrder(
  listPageFields: FieldConfig[],
): FieldConfig[] {
  const byName = new Map(listPageFields.map((f) => [f.field_name, f]));
  const groups = buildApiFieldGroups(listPageFields);
  const picked: FieldConfig[] = [];
  const seenRowKeys = new Set<string>();

  for (const canonicalName of LIST_TABLE_COLUMN_ORDER) {
    if (LIST_TABLE_EXCLUDED_FIELD_NAMES.has(canonicalName)) continue;

    const field = resolveListField(canonicalName, byName, groups);
    if (!field) continue;

    const rowKey = fieldNameToRowKey(field.field_name);
    if (seenRowKeys.has(rowKey)) continue;
    seenRowKeys.add(rowKey);
    picked.push(field);
  }

  for (const field of listPageFields) {
    if (LIST_TABLE_EXCLUDED_FIELD_NAMES.has(field.field_name)) continue;
    if (!isDynamicAutoTagsField(field)) continue;

    const rowKey = fieldNameToRowKey(field.field_name);
    if (seenRowKeys.has(rowKey)) continue;

    const candidates = groups.get(rowKey);
    if (!candidates?.length) continue;

    seenRowKeys.add(rowKey);
    picked.push(pickPreferredListField(candidates));
  }

  return picked;
}

export function sortListTableFields(fields: FieldConfig[]): FieldConfig[] {
  return pickListTableFieldsInOrder(fields);
}

/** Ordered row keys for columns present in the built set (fixed order first). */
export function buildGridOrderedRowKeys(columnRowKeys: string[]): string[] {
  const present = new Set(columnRowKeys);
  const ordered: string[] = [];

  for (const name of LIST_TABLE_COLUMN_ORDER) {
    const key = fieldNameToRowKey(name);
    if (present.has(key) && key !== "actions" && key !== "__check__") {
      ordered.push(key);
    }
  }

  for (const key of columnRowKeys) {
    if (key === "actions" || key === "__check__" || ordered.includes(key)) continue;
    ordered.push(key);
  }

  return ordered;
}

const LIST_TABLE_FIXED_ORDER_FIELDS = new Set(["__check__", "actions"]);

/**
 * Merge a user (or default) column order with currently available fields.
 * Keeps checkbox first and Actions last; appends newly appeared middle columns.
 */
export function mergeListTableColumnOrder(
  availableFields: readonly string[],
  preferredOrder?: readonly string[] | null,
): string[] {
  const availableMiddle = availableFields.filter(
    (field) => !LIST_TABLE_FIXED_ORDER_FIELDS.has(field),
  );
  const preferredMiddle = (preferredOrder ?? []).filter(
    (field) =>
      !LIST_TABLE_FIXED_ORDER_FIELDS.has(field) &&
      availableMiddle.includes(field),
  );
  const missingMiddle = availableMiddle.filter(
    (field) => !preferredMiddle.includes(field),
  );

  const next: string[] = [];
  if (availableFields.includes("__check__")) {
    next.push("__check__");
  }
  next.push(...preferredMiddle, ...missingMiddle);
  if (availableFields.includes("actions")) {
    next.push("actions");
  }
  return next;
}

/**
 * Initial visibility:
 * - Known product columns: LIST_TABLE_DEFAULT_VISIBLE_FIELD_NAMES
 * - Dynamic auto-tag columns: FieldConfig.default_display (explicit true/false)
 */
export function buildDefaultListColumnVisibilityModel(
  columnRowKeys: Array<string | undefined>,
  listPageFields?: FieldConfig[],
): GridColumnVisibilityModel {
  const model: GridColumnVisibilityModel = {};
  const fieldsByRowKey = buildListFieldByRowKey(listPageFields);

  for (const rowKey of columnRowKeys) {
    if (!rowKey || rowKey === "actions" || rowKey === "__check__") continue;
    if (LIST_TABLE_DEFAULT_VISIBLE_ROW_KEYS.has(rowKey)) continue;

    const field = fieldsByRowKey.get(rowKey);
    if (field && isDynamicAutoTagsField(field)) {
      model[rowKey] = Boolean(field.default_display);
      continue;
    }

    model[rowKey] = false;
  }
  return model;
}

/**
 * When fields API adds columns the user has never toggled, apply the same
 * default visibility as buildDefaultListColumnVisibilityModel without wiping
 * existing prefs. Returns null when nothing to merge.
 */
export function mergeHiddenDefaultsForNewListColumns(
  columnRowKeys: Array<string | undefined>,
  stored: GridColumnVisibilityModel,
  listPageFields?: FieldConfig[],
): GridColumnVisibilityModel | null {
  const defaults = buildDefaultListColumnVisibilityModel(
    columnRowKeys,
    listPageFields,
  );
  let changed = false;
  const next: GridColumnVisibilityModel = { ...stored };

  for (const [rowKey, visible] of Object.entries(defaults)) {
    if (Object.prototype.hasOwnProperty.call(next, rowKey)) continue;
    next[rowKey] = visible;
    changed = true;
  }

  return changed ? next : null;
}

/** List table header label; corrects API typos for known columns. */
export function getListTableColumnHeaderName(
  field: FieldConfig,
  rowKey: string,
): string {
  return rowKey === "receivedDate" ? "Received Date" : field.display_name;
}

/** Label for a column row in the custom Manage Columns panel. */
export function getListTableManageColumnLabel(
  field: string,
  headerName?: string | null,
): string {
  if (field === "receivedDate") return "Received Date";
  const trimmed = headerName?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : field;
}

/**
 * Map Data Grid column `field` (row key) → API `filters.conditions[].field`
 * for columns whose fields API `filter_field_name` references auto_tags
 * (e.g. ProductType → "auto_tags.ProductType").
 */
export function buildListGridFilterApiFieldMap(
  listPageFields: ReadonlyArray<FieldConfig> | null | undefined,
): Record<string, string> {
  const map: Record<string, string> = {};
  if (!listPageFields?.length) return map;

  for (const field of listPageFields) {
    const filterField =
      typeof field.filter_field_name === "string"
        ? field.filter_field_name.trim()
        : "";
    if (!filterField) continue;
    if (!isDynamicAutoTagsField(field)) continue;
    map[fieldNameToRowKey(field.field_name)] = filterField;
  }
  return map;
}

/** Columns that stay visible in Manage Columns and the filter-panel column list. */
export const LIST_TABLE_COLUMN_VISIBILITY_LOCKED_FIELDS = new Set([
  "id",
  "title",
]);

export function isListTableColumnShownInManageColumns(
  field: string,
  columnVisibilityModel: GridColumnVisibilityModel,
): boolean {
  if (!field || field === "actions" || field === "__check__") return false;
  if (LIST_TABLE_COLUMN_VISIBILITY_LOCKED_FIELDS.has(field)) return true;
  return columnVisibilityModel[field] !== false;
}

/** Filter panel "Columns" dropdown: same visibility as Manage Columns. */
export function filterListTableFieldsForColumnFilterPanel(
  columns: ReadonlyArray<{ field?: string }>,
  columnVisibilityModel: GridColumnVisibilityModel,
): string[] {
  return columns
    .filter(
      (col): col is { field: string } =>
        col.field != null &&
        isListTableColumnShownInManageColumns(col.field, columnVisibilityModel),
    )
    .map((col) => col.field);
}
